/**
 * File: src/kit/CoachMark.jsx
 * Purpose: Reusable “Tap here” coach mark with animated pointer (hand/arrow).
 * FinTorro Module: UI/Guidance
 *
 * Key Responsibilities
 * - Visually guide users to an (x,y) target in the chart overlay.
 * - Render a high-contrast pill label + animated pointer.
 * - Keep pointer aligned with label regardless of animations.
 *
 * Inputs
 * - target:   {x:number,y:number}  → where ripples point (e.g., crossover, start point)
 * - tail?:    {x:number,y:number}  → where pill + pointer live; if omitted we offset from target
 * - label?:   string                → pill text (“Tap here” by default)
 * - variant?: "hand"|"arrow"       → pointer style (default "hand")
 * - anchorOffset?: {dx,dy}         → only used if tail not provided (default {-64,-64})
 * - bounds?:  {w,h,pad}            → clamp tail inside overlay (prevents off-canvas)
 * - show?:    boolean              → toggle visibility
 * - soundOnShow?: boolean          → subtle blip on show
 *
 * Outputs
 * - Visual only; parent controls show/hide and behavior.
 *
 * Dependencies
 * - React 18; rendered inside the same SVG as <OverlayCanvas>.
 *
 * Accessibility Notes
 * - aria-hidden is set because this is decorative; instructional text mirrors in UI copy if needed.
 * - Respects prefers-reduced-motion for users sensitive to animation.
 *
 * Security/Privacy Notes
 * - No network; no external assets; no user data.
 *
 * Testing Notes
 * - Snapshot for presence of .ft-hand-bounce / .ft-arrow-bounce.
 * - Verify tail clamping by passing tiny bounds.
 *
 * Changelog
 * - 2025-08-29: v2.1. Fix transform stacking by using static outer translate + inner bounce.
 * Author: ChatGPT (FinTorro)
 */

import React, { useEffect } from "react";
import PropTypes from "prop-types";

/** tiny WebAudio beep (safe no-op if unsupported) */
function beep(freq = 740, dur = 0.06, vol = 0.12) {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "sine"; osc.frequency.value = freq;
    g.gain.value = vol;
    osc.connect(g).connect(ctx.destination);
    osc.start(); osc.stop(ctx.currentTime + dur);
    osc.onended = () => ctx.close();
  } catch {}
}

/** Keyframe injector (include once per lesson) */
export function CoachMarkStyles() {
  return (
    <style>{`
      .ft-glow { animation: ftGlow 1.4s ease-out infinite; opacity: .8 }
      .ft-glow.delay { animation-delay: .35s }
      @keyframes ftGlow { 0%{transform:scale(.85);opacity:.5}60%{transform:scale(1.15);opacity:.12}100%{transform:scale(1.22);opacity:0} }

      /* bounce is applied to INNER groups only; outer <g> holds the static translate */
      .ft-hand-bounce { transform-origin: center; animation: ftHand 1.1s cubic-bezier(.2,.9,.3,1) infinite }
      @keyframes ftHand { 0%,100%{ transform: translateY(-2px) } 50%{ transform: translateY(6px) } }

      .ft-arrow-bounce { transform-origin: center; animation: ftArrow 1.1s cubic-bezier(.2,.9,.3,1) infinite }
      @keyframes ftArrow { 0%,100%{ transform: translateY(-2px) } 50%{ transform: translateY(5px) } }

      .ft-tap-ripple { animation: ftTap 1.1s ease-out infinite }
      @keyframes ftTap { 0%{r:0;opacity:0} 30%{r:10px;opacity:.5} 60%{r:18px;opacity:.12} 100%{r:22px;opacity:0} }

      @media (prefers-reduced-motion: reduce) {
        .ft-glow, .ft-hand-bounce, .ft-arrow-bounce, .ft-tap-ripple { animation: none !important }
      }
    `}</style>
  );
}

/** Main component */
export function CoachMark({
  target,
  tail,
  label = "Tap here",
  variant = "hand",
  anchorOffset = { dx: -64, dy: -64 },
  bounds, // {w,h,pad}
  show = true,
  soundOnShow = false,
}) {
  useEffect(() => { if (show && soundOnShow) beep(); }, [show, soundOnShow]);
  if (!show || !target) return null;

  // --- Logic Note: compute final tail position with clamping ---
  // Inputs: target, optional tail, optional bounds.
  // Rule: use explicit tail when provided; otherwise offset from target.
  // Edge cases: clamp inside overlay minus padding to prevent clipping.
  const pad = bounds?.pad ?? 10;
  const rawTailX = tail?.x ?? (target.x + (anchorOffset?.dx ?? -64));
  const rawTailY = tail?.y ?? (target.y + (anchorOffset?.dy ?? -64));
  const tailX = bounds ? Math.min(Math.max(pad, rawTailX), bounds.w - pad) : rawTailX;
  const tailY = bounds ? Math.min(Math.max(pad, rawTailY), bounds.h - pad) : rawTailY;

  // local vector from tail → target (for arrow lines when we use an inner translate)
  const dx = target.x - tailX;
  const dy = target.y - tailY;

  // pill sizing
  const pillW = Math.max(112, label.length * 7.5);
  const pillH = 28;

  return (
    <g style={{ pointerEvents: "none" }} aria-hidden="true">
      {/* target ripples (always behind) */}
      <circle cx={target.x} cy={target.y} r="6"  fill="none" stroke="#FFB800" strokeWidth="3" />
      <circle cx={target.x} cy={target.y} r="14" fill="none" stroke="#FFB800" strokeWidth="2" className="ft-glow" />
      <circle cx={target.x} cy={target.y} r="22" fill="none" stroke="#FFB800" strokeWidth="1.5" className="ft-glow delay" />
      <circle cx={target.x} cy={target.y} r="0"  fill="none" stroke="#FFB800" strokeWidth="2" className="ft-tap-ripple" />

      {/* pill FIRST so pointer draws on top; positioned above tail */}
      <g transform={`translate(${tailX - pillW / 2}, ${tailY - pillH - 12})`}>
        <rect width={pillW} height={pillH} rx="14" ry="14"
              fill="#FF7C24" stroke="#FFB800" strokeWidth="1.5"
              opacity="0.98" filter="url(#ft-shadow)"/>
        <text x={pillW/2} y={pillH/2 + 4} textAnchor="middle"
              fontSize="12" fontWeight="900" fill="#0F141B"
              style={{ fontFamily: "Nunito Sans, system-ui, sans-serif" }}>
          {label}
        </text>
      </g>

      {/* POINTER: static outer translate + inner group with bounce (avoids transform override) */}
      {variant === "arrow" ? (
        <g transform={`translate(${tailX}, ${tailY})`}>
          <g className="ft-arrow-bounce">
            <line x1="0" y1="0" x2={dx - 10} y2={dy - 10}
                  stroke="#FFB800" strokeWidth="3" strokeDasharray="4 3" strokeLinecap="round"/>
            <polygon points={`${dx - 10},${dy - 16} ${dx},${dy} ${dx - 16},${dy - 8}`} fill="#FFB800" opacity="0.95"/>
          </g>
        </g>
      ) : (
        <g transform={`translate(${tailX}, ${tailY})`}>
          <g className="ft-hand-bounce">
            {/* halo for contrast */}
            <circle cx="10" cy="-6" r="16" fill="#FFFFFF" opacity="0.95" />
            {/* emoji hand (big, legible) */}
            <text x="-4" y="6" fontSize="28">👆</text>
          </g>
        </g>
      )}

      {/* soft shadow used by pill */}
      <defs>
        <filter id="ft-shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="#000" floodOpacity="0.35" />
        </filter>
      </defs>
    </g>
  );
}

CoachMark.propTypes = {
  target: PropTypes.shape({ x: PropTypes.number.isRequired, y: PropTypes.number.isRequired }).isRequired,
  tail:   PropTypes.shape({ x: PropTypes.number, y: PropTypes.number }),
  label:  PropTypes.string,
  variant:PropTypes.oneOf(["hand","arrow"]),
  anchorOffset: PropTypes.shape({ dx: PropTypes.number, dy: PropTypes.number }),
  bounds: PropTypes.shape({ w: PropTypes.number, h: PropTypes.number, pad: PropTypes.number }),
  show:   PropTypes.bool,
  soundOnShow: PropTypes.bool,
};
